import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { adminApi } from '../utils/adminApi';


// Upload a new discovery document. After upload, redirects to the workspace
// with the extracted questions already populated.
//
// All the caption fields are optional at upload — they can be filled in later
// in the workspace. The only required fields are client + file.
//
// Optional URL param: ?clientId=<uuid>  — pre-selects that client and locks
// the picker (used when arriving from a client detail page).

const TN_DEFAULT_COURT = 'CIRCUIT COURT FOR HAMILTON COUNTY, TENNESSEE';

export default function AdminDiscoveryResponseUploadPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedClientId = searchParams.get('clientId') || '';


  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({
    client_id: preselectedClientId,
    title: '',
    caption_court: TN_DEFAULT_COURT,
    plaintiff: '',
    defendant: '',
    docket_no: '',
    division: '',
    ryan_represents: 'defendant',
    opposing_counsel: ''
  });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [ocrNeeded, setOcrNeeded] = useState(false);

  useEffect(() => {
    adminApi.listClients().then(setClients).catch(() => {});
  }, []);

  function set(k, v) {setForm((p) => ({ ...p, [k]: v }));}

  function handleFile(e) {
    const f = e.target.files?.[0] || null;
    setFile(f);
    setOcrNeeded(false);
    setError('');
  }

  async function handleUpload(e) {
    e.preventDefault();
    setError('');
    setOcrNeeded(false);
    if (!form.client_id) {setError('Please select a client.');return;}
    if (!file) {setError('Please choose a file to upload.');return;}

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      Object.entries(form).forEach(([k, v]) => {
        if (v !== '' && v != null) fd.append(k, v);
      });
      const data = await adminApi.uploadDiscoveryResponse(fd);
      navigate(`/admin/dashboard/discovery-responses/${data.response.id}`);
    } catch (err) {
      if (err.code === 'OCR_REQUIRED') {
        setOcrNeeded(true);
        setError('');
      } else {
        setError(err.message);
      }
    } finally {
      setUploading(false);
    }
  }

  const selectedClient = clients.find((c) => c.id === form.client_id);

  return (
    <div>
      <div style={{ marginBottom: 8 }}>
        <button className="btn btn-sm" onClick={() => navigate('/admin/dashboard/discovery-responses')} style={{ marginBottom: 12 }}>
          ← Back to all responses
        </button>
        <h1 className="page-title">Upload Discovery Document</h1>
        <p className="page-subtitle">Upload the discovery document received from opposing counsel. The system will extract the questions, and you can review and edit them before answering.</p>
      </div>

      {error && <div className="alert alert-error" style={{ whiteSpace: 'pre-line' }}>{error}</div>}

      {ocrNeeded &&
      <div className="alert" style={{
        background: '#fff8e1', color: '#5b4a08', border: '1px solid #f0d68a',
        padding: 14, borderRadius: 4, marginBottom: 16, fontFamily: 'Arial, sans-serif', fontSize: 13
      }}>
          <strong>This PDF appears to be a scanned image and is not yet OCR'd.</strong>
          <div style={{ marginTop: 8 }}>
            To extract questions, the PDF needs searchable text. Open it in <strong>Adobe Acrobat</strong> (Tools → Scan &amp; OCR → Recognize Text), <strong>macOS Preview</strong>, or your scanner software, save the OCR'd copy, and re-upload it here.
          </div>
        </div>
      }

      <form onSubmit={handleUpload}>
        {/* Client + file */}
        <div className="section-head" style={{ marginTop: 0 }}>The Basics</div>
        <div className="card">
          <div className="form-row">
            <div className="form-group">
              <label>Client *</label>
              {preselectedClientId ?
              <input
                type="text"
                value={selectedClient ? selectedClient.name : 'Loading...'}
                disabled
                style={{ background: '#f5f5f5', color: '#555' }} /> :


              <select value={form.client_id} onChange={(e) => set('client_id', e.target.value)} required>
                  <option value="">— select a client —</option>
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              }
            </div>
            <div className="form-group">
              <label>Title (optional)</label>
              <input type="text" value={form.title} onChange={(e) => set('title', e.target.value)}
              placeholder="e.g. Plaintiff's First Set of Interrogatories" />
            </div>
          </div>
          <div className="form-group">
            <label>Discovery file *</label>
            <input type="file" accept=".pdf,.docx" onChange={handleFile} />
            {file &&
            <div style={{ fontSize: 12, color: '#666', marginTop: 6, fontFamily: 'Arial, sans-serif' }}>
                Selected: <strong>{file.name}</strong> ({(file.size / 1024).toFixed(0)} KB)
              </div>
            }
            <div style={{ fontSize: 11, color: '#888', marginTop: 6, fontFamily: 'Arial, sans-serif' }}>
              Accepts PDF and .docx. If your PDF is a scan, OCR it first (in Acrobat or Preview) before uploading.
            </div>
          </div>
        </div>

        {/* Caption — optional at upload, can be set later in workspace */}
        {/* Note: these are EDIT inputs so they intentionally show real values
             for safe editing/saving even in privacy mode. The banner reminds. */}
        <div className="section-head">Case Caption (optional now — can edit later)</div>
        <div className="card">
          <div className="form-group">
            <label>Court</label>
            <input type="text" value={form.caption_court} onChange={(e) => set('caption_court', e.target.value)} />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Plaintiff</label>
              <input type="text" value={form.plaintiff} onChange={(e) => set('plaintiff', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Defendant</label>
              <input type="text" value={form.defendant} onChange={(e) => set('defendant', e.target.value)} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Docket No.</label>
              <input type="text" value={form.docket_no} onChange={(e) => set('docket_no', e.target.value)} placeholder="___-D-____" />
            </div>
            <div className="form-group">
              <label>Division</label>
              <input type="text" value={form.division} onChange={(e) => set('division', e.target.value)} placeholder="__" />
            </div>
            <div className="form-group">
              <label>We Represent</label>
              <select value={form.ryan_represents} onChange={(e) => set('ryan_represents', e.target.value)}>
                <option value="defendant">Defendant</option>
                <option value="plaintiff">Plaintiff</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label>Opposing Counsel (name &amp; address for COS)</label>
            <textarea value={form.opposing_counsel} onChange={(e) => set('opposing_counsel', e.target.value)}
            rows={3}
            placeholder={'Jane Doe, Esq.\nDoe & Associates\n123 Main St., Suite 200\nCity, ST 00000'}
            style={{ width: '100%', fontFamily: 'Arial, sans-serif', fontSize: 13, padding: 8, lineHeight: 1.5 }} />
            
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <button type="submit" className="btn btn-primary" disabled={uploading || !form.client_id || !file}>
            {uploading ? <><span className="spinner" />Uploading &amp; extracting questions...</> : 'Upload and Extract Questions'}
          </button>
          <button type="button" className="btn" onClick={() => navigate('/admin/dashboard/discovery-responses')} disabled={uploading}>
            Cancel
          </button>
        </div>

        {uploading &&
        <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 12, color: '#888', marginTop: 10 }}>
            This takes 5–30 seconds depending on the document size. The AI is identifying every interrogatory, RFP, and RFA.
          </p>
        }

        {selectedClient &&
        <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 11, color: '#888', marginTop: 16 }}>
            Note: the original file is not stored on the server. Only the extracted text becomes question rows you can edit.
          </p>
        }
      </form>
    </div>);

}